import type {
    AgencyClientRow,
    AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';
import type { TranslationKey } from '@/types/i18n.types';

/**
 * Export XLSX della lista clienti: una riga per cliente.
 *
 * Vale la stessa regola del dossier del singolo cliente (`client-dossier.export.ts`): qui
 * non si somma e non si conta nulla. Ogni numero della riga -- viaggi, date di partenza,
 * totale incassato -- arriva gia' calcolato da `get_agency_clients`, che aggrega nel
 * database sullo stesso perimetro RLS della tabella a schermo. Un totale ricavato qui
 * divergerebbe in silenzio da quello mostrato accanto.
 *
 * Il foglio esporta le righe che la lista ha in mano, filtri compresi: il file deve
 * coincidere con quello che l'utente sta guardando. Il secondo foglio dichiara quali
 * filtri erano attivi, cosi' chi riceve il file per email non lo scambia per l'anagrafica
 * completa.
 */

/** Firma di `t` da `useI18n`, presa come parametro per tenere il builder fuori da React. */
export type ClientsListT = (key: TranslationKey, options?: Record<string, unknown>) => string;

export interface BuildClientsListOptions {
    t: ClientsListT;
    /** Periodo selezionato nella pagina: e' il filtro con cui la RPC ha prodotto le righe. */
    period: AgencyDashboardPeriod;
    /** Testo di ricerca attivo, se c'e'. */
    search?: string;
    /** Nome del consulente filtrato, se il filtro e' attivo. */
    advisorName?: string | null;
    /** Lingua per le date scritte come testo. Le date vere restano Date, non stringhe. */
    locale?: string;
}

/*
  Lo spazio prima dell'euro e' unificatore (U+00A0), scritto come escape e non come
  carattere: nel sorgente sarebbe indistinguibile da uno spazio normale, e chi passasse di
  qui a "sistemare" la spaziatura lo sostituirebbe senza accorgersene, lasciando che Excel
  mandi a capo il simbolo di valuta quando la colonna e' stretta.
*/
export const MONEY_FORMAT = '#,##0.00\u00A0€';
export const DATE_FORMAT = 'dd/mm/yyyy';

/**
 * `YYYY-MM-DD` -> Date locale a mezzogiorno.
 *
 * `new Date('2026-09-12')` e' mezzanotte UTC: a ovest di Greenwich Excel mostrerebbe l'11.
 * Mezzogiorno tiene la data ferma su qualunque fuso, e l'ora non viene mai mostrata perche'
 * la colonna e' formattata come data secca.
 */
export const parseDateOnly = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return null;
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0);
};

/**
 * Il consulente che finisce nella cella.
 *
 * Non solo il principale: un cliente puo' essere passato per piu' mani, e in un foglio
 * destinato all'amministrazione nascondere gli altri renderebbe impossibile capire a chi
 * chiedere conto delle sue pratiche. Sono ordinati per coinvolgimento dalla RPC, quindi il
 * primo nome resta quello di riferimento anche qui.
 */
const advisorsLabel = (row: AgencyClientRow): string => {
    const names = (row.advisors ?? [])
        .map((advisor) => [advisor.first_name, advisor.last_name].filter(Boolean).join(' ').trim())
        .filter((name) => name.length > 0);

    if (names.length > 0) return names.join(', ');

    // Fallback sui campi piatti: `advisors` e' vuoto quando nessuna pratica ha un
    // consulente assegnato, ma i due campi principali possono comunque essere valorizzati.
    return [row.advisor_first_name, row.advisor_last_name].filter(Boolean).join(' ');
};

interface SheetColumn {
    header: string;
    width: number;
    /** Formato numerico Excel della colonna. Nessun calcolo: solo come si vede il numero. */
    numFmt?: string;
}

/** Intestazione in grassetto su fondo chiaro, come nel dossier del singolo cliente. */
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } } as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ws = any;

/**
 * Costruisce il workbook e lo restituisce come Blob pronto al download.
 *
 * `exceljs` entra con un import dinamico: e' quasi un megabyte di bundle e serve solo a chi
 * preme il bottone, non a chi apre la dashboard.
 */
export async function buildClientsListWorkbook(
    rows: AgencyClientRow[],
    { t, period, search, advisorName, locale = 'it-IT' }: BuildClientsListOptions
): Promise<Blob> {
    const module = await import('exceljs');
    // Il bundle browser di exceljs esporta il namespace come default, quello node no.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ExcelJS = ((module as any).default ?? module) as any;

    const workbook = new ExcelJS.Workbook();
    const generatedAt = new Date();
    workbook.created = generatedAt;
    workbook.views = [{ activeTab: 0, firstSheet: 0, visibility: 'visible' }];

    // ------------------------------------------------------------------ Clienti (foglio 1)
    /*
      Foglio tabellare puro: intestazione e poi una riga per cliente, senza titoli, note in
      coda o righe di totale. Chi riceve questo file lo apre per filtrarlo, ordinarlo o
      importarlo altrove, e una riga di testo in mezzo ai dati rompe tutte e tre le cose.
      Il contesto sta nel secondo foglio proprio per questo.
    */
    const sheet = workbook.addWorksheet(t('dashboard.overview.clients.exportList.sheetClients'));

    const columns: SheetColumn[] = [
        { header: t('dashboard.overview.clients.exportList.columnFirstName'), width: 18 },
        { header: t('dashboard.overview.clients.exportList.columnLastName'), width: 18 },
        { header: t('dashboard.overview.clients.exportList.columnEmail'), width: 30 },
        { header: t('dashboard.overview.clients.exportList.columnPhone'), width: 18 },
        { header: t('dashboard.overview.clients.exportList.columnClientType'), width: 16 },
        { header: t('dashboard.overview.clients.exportList.columnAdvisor'), width: 26 },
        { header: t('dashboard.overview.clients.exportList.columnTrips'), width: 11 },
        { header: t('dashboard.overview.clients.exportList.columnLastTrip'), width: 15, numFmt: DATE_FORMAT },
        { header: t('dashboard.overview.clients.exportList.columnNextTrip'), width: 15, numFmt: DATE_FORMAT },
        { header: t('dashboard.overview.clients.exportList.columnTotalSpent'), width: 16, numFmt: MONEY_FORMAT },
        { header: t('dashboard.overview.clients.exportList.columnNotes'), width: 40 },
    ];

    sheet.columns = columns.map((column) => ({ width: column.width }));

    const header = sheet.addRow(columns.map((column) => column.header));
    header.font = { bold: true };
    header.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    rows.forEach((client) => {
        const row = sheet.addRow([
            client.first_name ?? '',
            client.last_name ?? '',
            client.email ?? '',
            client.phone ?? '',
            // Tipo cliente e Note non hanno oggi una sorgente: nel database non esiste ne'
            // una classificazione del cliente ne' un campo note sull'anagrafica. La colonna
            // c'e' e resta vuota, da compilare a mano sul file scaricato; il giorno in cui
            // il dato esistera' bastera' riempirla qui, senza rifare il tracciato.
            '',
            advisorsLabel(client),
            client.trips_count,
            parseDateOnly(client.last_trip_at) ?? '',
            parseDateOnly(client.next_trip_at) ?? '',
            // Totale speso = quanto il cliente ha davvero versato sulle rate, non quanto gli
            // e' stato preventivato: e' `paid_amount`, la stessa cifra della colonna
            // "pagato" nella lista.
            client.paid_amount,
            '',
        ]);

        columns.forEach((column, index) => {
            if (column.numFmt) row.getCell(index + 1).numFmt = column.numFmt;
        });
    });

    // Intestazione bloccata: la lista puo' essere lunga qualche centinaio di righe e senza
    // il blocco si perde di vista quale colonna si sta leggendo.
    sheet.views = [{ state: 'frozen', ySplit: header.number }];
    sheet.autoFilter = {
        from: { row: header.number, column: 1 },
        to: { row: header.number, column: columns.length },
    };

    // ------------------------------------------------------------------ Contesto (foglio 2)
    /*
      Con quali filtri e' stato prodotto il file. Serve perche' l'export segue la lista: un
      foglio generato con la ricerca attiva contiene un sottoinsieme, e senza questa pagina
      sarebbe indistinguibile dall'anagrafica completa una volta uscito dall'applicazione.
    */
    const context = workbook.addWorksheet(t('dashboard.overview.clients.exportList.sheetContext'));
    context.columns = [{ width: 30 }, { width: 60 }];

    const contextHeader = context.addRow([
        t('dashboard.overview.clients.export.fieldColumn'),
        t('dashboard.overview.clients.export.valueColumn'),
    ]);
    contextHeader.font = { bold: true };
    contextHeader.eachCell((cell: Ws) => {
        cell.fill = HEADER_FILL;
    });

    // Le chiavi a schermo sono period7d / period30d / period90d / periodYtd.
    const periodLabel = t(
        `dashboard.overview.period${period === 'ytd' ? 'Ytd' : period}` as TranslationKey
    );

    context.addRow([t('dashboard.overview.clients.export.generatedOnLabel'), generatedAt.toLocaleString(locale)]);
    context.addRow([t('dashboard.overview.clients.export.periodLabel'), periodLabel]);
    context.addRow([
        t('dashboard.overview.clients.exportList.contextSearch'),
        search?.trim() || t('dashboard.overview.clients.exportList.contextNoFilter'),
    ]);
    context.addRow([
        t('dashboard.overview.clients.exportList.contextAdvisor'),
        advisorName || t('dashboard.overview.clients.exportList.contextAllAdvisors'),
    ]);
    context.addRow([t('dashboard.overview.clients.exportList.contextCount'), rows.length]);

    const note = context.addRow([t('dashboard.overview.clients.exportList.contextNote')]);
    note.font = { italic: true, color: { argb: 'FF6B7280' }, size: 9 };

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
}

/** Nome file: `clienti_2026-09-08.xlsx`. */
export function clientsListFilename(prefix: string): string {
    const day = new Date().toISOString().slice(0, 10);
    return `${prefix}_${day}.xlsx`;
}
